// ⚠️ AUTO-GENERATED FROM CORE REPO
// DO NOT EDIT — changes will be overwritten

/*
  Enhances core.product-card with cart-aware quantity controls.

  When a product variant is already in the cart, the card swaps the Add/View
  buttons for quantity controls. Quantity changes are synced to Shopify cart
  lines, and the Add/View buttons return when quantity reaches 0.
*/

(function () {
  const GREEN = "#2D793A";
  const DEBOUNCE_DELAY = 1000;
  const CART_POLL_INTERVAL = 2000;
  const pendingUpdates = new Map();
  const activeAdds = new Set();

  // Let cart/checkout links finish debounced card edits before navigating.
  window.CoreProductCardCart = {
    async flush() {
      const results = await Promise.all([
        ...activeAdds,
        ...[...pendingUpdates.values()].map((pending) => {
          clearTimeout(pending.timeout);
          return pending.run();
        }),
      ]);
      return results.every((result) => result !== false);
    },
  };

  let lastCartState = null;
  let pollInterval = null;

  function itemHasBundleIdProperty(item) {
    const p = item.properties;

    if (!p) return false;

    if (Array.isArray(p)) {
      return p.some((x) => x.name === "_bundle_id" && x.value != null && String(x.value) !== "");
    }

    if (typeof p === "object") {
      return p._bundle_id != null && String(p._bundle_id) !== "";
    }

    return false;
  }

  function itemIsCoreProductCardLine(item) {
    const p = item.properties;

    if (!p) return false;

    if (Array.isArray(p)) {
      return p.some((x) => x.name === "_core_product_card" && (x.value === "1" || x.value === true));
    }

    if (typeof p === "object") {
      return p._core_product_card === "1" || p._core_product_card === true;
    }

    return false;
  }

  function itemIgnoredForCoreProductCard(item) {
    if (!itemHasBundleIdProperty(item)) return false;
    return !itemIsCoreProductCardLine(item);
  }

  function getManagedLinesForVariant(cart, variantId) {
    if (!cart?.items?.length) return [];
    return cart.items.filter((item) => item.variant_id === variantId && !itemIgnoredForCoreProductCard(item));
  }

  function getDisplayQuantity(cart, variantId) {
    return getManagedLinesForVariant(cart, variantId).reduce((sum, item) => sum + (item.quantity || 0), 0);
  }

  document.addEventListener("DOMContentLoaded", init);

  function init() {
    const cards = [...document.querySelectorAll(".product-card")];

    if (!cards.length) return;

    cards.forEach((card) => setAtcLoading(card, true));

    attachAddInterceptors(cards);
    setupCartEventListeners();
    startCartPolling();

    fetchCart()
      .then((cart) => {
        lastCartState = cart;

        cards.forEach((card) => {
          const variantId = getVariantIdFromCard(card);

          if (!variantId) return;

          const qty = getDisplayQuantity(cart, variantId);

          if (qty > 0) {
            renderQty(card, variantId, qty);
          }
        });
      })
      .catch(() => {
        console.warn("[core.product-card] Cart fetch failed; leaving default buttons.");
      })
      .finally(() => {
        cards.forEach((card) => setAtcLoading(card, false));
      });
  }

  function startCartPolling() {
    pollInterval = setInterval(async () => {
      try {
        const cart = await fetchCart();
        checkAndUpdateFromCart(cart);
      } catch (e) {
        console.warn("[core.product-card] Cart poll failed", e);
      }
    }, CART_POLL_INTERVAL);
  }

  function checkAndUpdateFromCart(cart) {
    if (!cart || !cart.items) return;

    document.querySelectorAll(".product-card").forEach((card) => {
      const variantId = getVariantIdFromCard(card);

      if (!variantId) return;

      if (pendingUpdates.has(variantId)) {
        const pending = pendingUpdates.get(variantId);

        if (pending.targetQuantity !== 0) {
          return;
        }
      }

      const newQty = getDisplayQuantity(cart, variantId);
      const el = qInCard(card, ".product-card-qty");
      const currentDisplayQty = el ? parseInt(el.querySelector(".product-card-qty-num")?.textContent, 10) : 0;

      if (newQty !== currentDisplayQty) {
        if (newQty > 0) {
          renderQty(card, variantId, newQty);
          hideView(card);
        } else {
          showAddHideQty(card);
          showView(card);
        }
      }
    });

    lastCartState = cart;
  }

  function setupCartEventListeners() {
    const handleCartUpdate = (e) => {
      const { variantId, quantity, cart } = e.detail || {};

      if (cart) {
        checkAndUpdateFromCart(cart);
        return;
      }

      if (variantId === undefined) return;

      document.querySelectorAll(".product-card").forEach((card) => {
        const v = getVariantIdFromCard(card);

        if (v !== variantId) return;
        if (pendingUpdates.has(variantId)) return;

        if (quantity > 0) {
          renderQty(card, variantId, quantity);
          hideView(card);
        } else {
          showAddHideQty(card);
          showView(card);
        }
      });
    };

    document.addEventListener("updated.core.product-card.cart", handleCartUpdate);
    window.addEventListener("updated.core.product-card.cart", handleCartUpdate);

    document.addEventListener("cart:updated", handleCartUpdate);
    window.addEventListener("cart:updated", handleCartUpdate);

    document.addEventListener("cart.changed", handleCartUpdate);
    window.addEventListener("cart.changed", handleCartUpdate);

    document.addEventListener("ajaxCart.afterCartLoad", handleCartUpdate);
    window.addEventListener("ajaxCart.afterCartLoad", handleCartUpdate);

    document.addEventListener("theme:cart:change", handleCartUpdate);
    window.addEventListener("theme:cart:change", handleCartUpdate);

    const refreshFromCartEvents = ["cart:refresh", "cart.requestComplete"];

    refreshFromCartEvents.forEach((eventName) => {
      const handler = async () => {
        try {
          const cart = await fetchCart();
          checkAndUpdateFromCart(cart);
        } catch (e) {
          console.warn("[core.product-card] Failed to refresh cart on event", e);
        }
      };

      document.addEventListener(eventName, handler);
      window.addEventListener(eventName, handler);
    });
  }

  function qInCard(card, sel) {
    return card.querySelector(sel);
  }

  function getVariantIdFromCard(card) {
    const input = qInCard(card, 'form.atc-form input[name="id"]');

    if (!input) return null;

    const v = parseInt(input.value, 10);

    return Number.isFinite(v) ? v : null;
  }

  function setAtcLoading(card, isLoading) {
    const btn = qInCard(card, "form.atc-form .atc-btn");
    const form = qInCard(card, "form.atc-form");

    if (!btn) return;

    btn.classList.toggle("is-loading", !!isLoading);

    if (isLoading) {
      btn.setAttribute("disabled", "");
      btn.setAttribute("aria-busy", "true");
    } else {
      btn.removeAttribute("aria-busy");

      if (form?.dataset.available === "true" && card.dataset.productAvailable === "true") {
        btn.removeAttribute("disabled");
      } else {
        btn.setAttribute("disabled", "");
      }
    }
  }

  function hideElement(el) {
    if (!el) return;

    el.classList.add("tw:hidden");
    el.style.setProperty("display", "none", "important");
  }

  function showElement(el) {
    if (!el) return;

    el.classList.remove("tw:hidden");
    el.style.removeProperty("display");
  }

  function hideAddShowQty(card) {
    const form = qInCard(card, "form.atc-form");
    const qty = qInCard(card, ".product-card-qty");

    hideElement(form);
    showElement(qty);
  }

  function showAddHideQty(card) {
    const form = qInCard(card, "form.atc-form");
    const qty = qInCard(card, ".product-card-qty");

    showElement(form);
    hideElement(qty);
    showView(card);
  }

  function hideView(card) {
    const view = qInCard(card, ".product-card-view");
    hideElement(view);
  }

  function showView(card) {
    const view = qInCard(card, ".product-card-view");
    showElement(view);
  }

  function ensureQtyContainer(card) {
    let el = qInCard(card, ".product-card-qty");

    if (el) return el;

    const actionsRow = qInCard(card, ".tw\\:mt-auto");

    if (!actionsRow) {
      const gridContainer = qInCard(card, ".tw\\:grid.tw\\:grid-cols-2");

      if (!gridContainer) return null;

      el = document.createElement("div");
      el.className = "product-card-qty tw:relative tw:z-30 tw:flex-1 tw:basis-0 tw:min-w-0 tw:col-span-2 tw:hidden";
      el.innerHTML = `
        <div class="tw:flex tw:items-center tw:justify-between tw:px-[12px] tw:py-[7px] tw:text-[18px] tw:leading-none tw:tracking-[0.18em] tw:uppercase tw:font-afacad tw:w-full"
             style="background:${GREEN}; color:white; border-radius: var(--product-card-qty-border-radius);">
          <button type="button" class="product-card-qty-btn tw:shrink-0 tw:px-3 tw:py-1 tw:cursor-pointer" data-act="dec" aria-label="Decrease">−</button>
          <div class="product-card-qty-num tw:select-none" aria-live="polite" aria-atomic="true">1</div>
          <button type="button" class="product-card-qty-btn tw:shrink-0 tw:px-3 tw:py-1 tw:cursor-pointer" data-act="inc" aria-label="Increase">+</button>
        </div>
      `;

      const view = qInCard(card, ".product-card-view");

      gridContainer.insertBefore(el, view || null);

      return el;
    }

    el = document.createElement("div");
    el.className = "product-card-qty tw:relative tw:z-30 tw:flex-1 tw:basis-0 tw:min-w-0 tw:col-span-2 tw:hidden";
    el.innerHTML = `
      <div class="tw:flex tw:items-center tw:justify-between tw:px-[12px] tw:py-[9px] tw:text-[18px] tw:leading-none tw:tracking-[0.18em] tw:uppercase tw:font-afacad tw:w-full"
           style="background:${GREEN}; color:white;">
        <button type="button" class="product-card-qty-btn tw:shrink-0 tw:px-3 tw:py-1 tw:cursor-pointer" data-act="dec" aria-label="Decrease">−</button>
        <div class="product-card-qty-num tw:select-none" aria-live="polite" aria-atomic="true">1</div>
        <button type="button" class="product-card-qty-btn tw:shrink-0 tw:px-3 tw:py-1 tw:cursor-pointer" data-act="inc" aria-label="Increase">+</button>
      </div>
    `;

    const view = qInCard(card, ".product-card-view");

    actionsRow.insertBefore(el, view || null);

    return el;
  }

  function debouncedUpdateCart(card, variantId, quantity) {
    const el = qInCard(card, ".product-card-qty");

    if (!el) return;

    const num = el.querySelector(".product-card-qty-num");

    if (pendingUpdates.has(variantId)) {
      clearTimeout(pendingUpdates.get(variantId).timeout);
    }

    num.textContent = String(quantity);

    let writePromise;
    const run = () => (writePromise ||= save());
    const save = async () => {
      const targetQty = quantity;

      if (targetQty === 0) {
        hideView(card);
      }

      num.innerHTML =
        '<div class="tw:inline-block tw:w-4 tw:h-4 tw:border-2 tw:border-white tw:border-t-transparent tw:rounded-full tw:animate-spin"></div>';

      el.querySelectorAll(".product-card-qty-btn").forEach((btn) => {
        btn.disabled = true;
      });

      try {
        const ok = await applyVariantQuantityTarget(variantId, targetQty);

        if (!ok) {
          try {
            const cart = await fetchCart();
            const actualQty = getDisplayQuantity(cart, variantId);

            if (actualQty === 0) {
              showAddHideQty(card);
              showView(card);
            } else {
              num.textContent = String(actualQty);
              el.querySelectorAll(".product-card-qty-btn").forEach((btn) => {
                btn.disabled = false;
              });
              hideView(card);
            }
          } catch (e) {
            console.error("[core.product-card] Failed to fetch cart after update error", e);

            num.textContent = String(targetQty);
            el.querySelectorAll(".product-card-qty-btn").forEach((btn) => {
              btn.disabled = false;
            });
          }

          return false;
        }

        if (targetQty === 0) {
          showAddHideQty(card);
          showView(card);
        } else {
          num.textContent = String(targetQty);
          el.querySelectorAll(".product-card-qty-btn").forEach((btn) => {
            btn.disabled = false;
          });
          hideView(card);
        }
        return true;
      } catch (e) {
        console.error("[core.product-card] Unexpected cart update error", e);

        num.textContent = String(targetQty);
        el.querySelectorAll(".product-card-qty-btn").forEach((btn) => {
          btn.disabled = false;
        });
        return false;
      } finally {
        pendingUpdates.delete(variantId);
      }
    };
    const timeoutId = setTimeout(run, DEBOUNCE_DELAY);

    pendingUpdates.set(variantId, {
      timeout: timeoutId,
      run,
      targetQuantity: quantity,
    });
  }

  function renderQty(card, variantId, quantity) {
    const el = ensureQtyContainer(card);

    if (!el) {
      console.error("[core.product-card] Could not create/find quantity container");
      return;
    }

    const num = el.querySelector(".product-card-qty-num");

    num.textContent = String(quantity);
    el.dataset.confirmedQty = String(quantity);

    const btns = el.querySelectorAll(".product-card-qty-btn");

    btns.forEach((btn) => {
      btn.disabled = false;

      btn.onclick = () => {
        const act = btn.dataset.act;
        const current = parseInt(num.textContent, 10) || 0;
        const next = act === "inc" ? current + 1 : Math.max(0, current - 1);

        debouncedUpdateCart(card, variantId, next);
      };
    });

    hideAddShowQty(card);
    hideView(card);
  }

  async function fetchCart() {
    const res = await fetch("/cart.js", {
      credentials: "same-origin",
    });

    if (!res.ok) throw new Error("cart.js failed");

    return res.json();
  }

  function dispatchCartUpdated(variantId, quantity, cart) {
    if (typeof window.upcartRefreshCart === "function") {
      window.upcartRefreshCart();
    }

    const eventDetail = {
      detail: {
        variantId,
        quantity,
        cart,
      },
      bubbles: true,
      cancelable: true,
    };

    document.dispatchEvent(new CustomEvent("updated.core.product-card.cart", eventDetail));
    window.dispatchEvent(new CustomEvent("updated.core.product-card.cart", eventDetail));
  }

  async function postChangeJson(res) {
    if (!res.ok) throw new Error("cart request failed");
    return res.json();
  }

  async function applyVariantQuantityTarget(variantId, targetQty) {
    try {
      let cart = await fetchCart();

      if (targetQty === 0) {
        const managed = getManagedLinesForVariant(cart, variantId);

        for (const line of managed) {
          cart = await postChangeJson(
            await fetch("/cart/change.js", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              credentials: "same-origin",
              body: JSON.stringify({
                id: line.key,
                quantity: 0,
              }),
            }),
          );
        }

        dispatchCartUpdated(variantId, 0, cart);

        return true;
      }

      let managed = getManagedLinesForVariant(cart, variantId);

      if (managed.length === 0) {
        const bundleId = crypto.randomUUID();

        await postChangeJson(
          await fetch("/cart/add.js", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "same-origin",
            body: JSON.stringify({
              id: variantId,
              quantity: targetQty,
              properties: {
                _bundle_id: bundleId,
                _core_product_card: "1",
              },
            }),
          }),
        );

        cart = await fetchCart();

        dispatchCartUpdated(variantId, getDisplayQuantity(cart, variantId), cart);

        return true;
      }

      for (let guard = 0; guard < 20; guard++) {
        const sum = getDisplayQuantity(cart, variantId);

        if (sum === targetQty) break;

        managed = getManagedLinesForVariant(cart, variantId);

        if (managed.length === 0) {
          const bundleId = crypto.randomUUID();

          await postChangeJson(
            await fetch("/cart/add.js", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              credentials: "same-origin",
              body: JSON.stringify({
                id: variantId,
                quantity: targetQty,
                properties: {
                  _bundle_id: bundleId,
                  _core_product_card: "1",
                },
              }),
            }),
          );

          cart = await fetchCart();
          break;
        }

        const first = managed[0];
        const rest = managed.slice(1);
        const restSum = rest.reduce((sum, line) => sum + (line.quantity || 0), 0);
        const desiredFirst = targetQty - restSum;

        if (desiredFirst >= 0) {
          cart = await postChangeJson(
            await fetch("/cart/change.js", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              credentials: "same-origin",
              body: JSON.stringify({
                id: first.key,
                quantity: desiredFirst,
              }),
            }),
          );

          break;
        }

        const last = managed[managed.length - 1];

        cart = await postChangeJson(
          await fetch("/cart/change.js", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "same-origin",
            body: JSON.stringify({
              id: last.key,
              quantity: 0,
            }),
          }),
        );
      }

      cart = await fetchCart();

      dispatchCartUpdated(variantId, getDisplayQuantity(cart, variantId), cart);

      return true;
    } catch (e) {
      console.error("[core.product-card] Cart update failed", e);
      return false;
    }
  }

  function attachAddInterceptors(cards) {
    const submit = async function (form, card) {
      if (form?.dataset.available !== "true" || card?.dataset.productAvailable !== "true") return false;

      const fd = new FormData(form);
      const variantId = parseInt(fd.get("id"), 10);

      if (!Number.isFinite(variantId)) return;

      form.dataset.busy = "1";
      setAtcLoading(card, true);

      try {
        const res = await fetch("/cart/add.js", {
          method: "POST",
          headers: {
            Accept: "application/json",
          },
          credentials: "same-origin",
          body: fd,
        });

        if (!res.ok) throw new Error("add.js failed");

        await res.json();

        const cart = await fetchCart();
        const actualQty = getDisplayQuantity(cart, variantId);

        ensureQtyContainer(card);

        if (actualQty > 0) {
          renderQty(card, variantId, actualQty);
          hideView(card);
        } else {
          showAddHideQty(card);
          showView(card);
        }

        const eventDetail = {
          detail: {
            variantId,
            quantity: actualQty,
            cart,
          },
          bubbles: true,
          cancelable: true,
        };

        document.dispatchEvent(new CustomEvent("updated.core.product-card.cart", eventDetail));
        window.dispatchEvent(new CustomEvent("updated.core.product-card.cart", eventDetail));

        if (typeof window.upcartRefreshCart === "function") {
          window.upcartRefreshCart();
        }
      } catch (e) {
        console.error("[core.product-card] add.js failed", e);
        return false;
      } finally {
        form.dataset.busy = "0";
        setAtcLoading(card, false);
      }
    };

    window.coreProductCardSubmitAtcForm = function (form, card) {
      const request = submit(form, card);
      activeAdds.add(request);
      request.finally(() => activeAdds.delete(request));
      return request;
    };

    cards.forEach((card) => {
      const form = qInCard(card, "form.atc-form");

      if (!form) return;

      form.onsubmit = function (evt) {
        evt.preventDefault();

        if (form.dataset.available !== "true" || card.dataset.productAvailable !== "true") return false;
        if (form.dataset.busy === "1") return false;

        window.coreProductCardSubmitAtcForm(form, card);

        return false;
      };
    });
  }

  window.addEventListener("beforeunload", () => {
    if (pollInterval) {
      clearInterval(pollInterval);
    }
  });
})();
